import { combineReducers, configureStore, type Store } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useSelector, useDispatch } from 'react-redux';
import { authSlice } from '@/store/slices/authSlice';
import { pauseSlice } from '@/store/slices/pauseSlice';
import { progressSlice } from '@/store/slices/progressSlice';
import { siteConfig } from '@/store/slices/siteConfig';
import { storiesSlice } from '@/store/slices/storiesSlice';
import { viewedStoriesSlice } from '@/store/slices/viewedStoriesSlice';
import { aiApi } from '@/api/aiApi/aiApi';
import { authApi } from '@/api/authApi/authApi';
import { authEmailApi } from '@/api/authEmailApi/authEmailApi';
import { citiesApi } from '@/api/citiesApi/citiesApi';
import { clothingApi } from '@/api/clothingApi/clothingApi';
import { eventsApi } from '@/api/eventsApi/eventsApi';
import { favoritesApi } from '@/api/favoritesApi/favoritesApi';
import { filesApi } from '@/api/filesApi/filesApi';
import { outfitsApi } from '@/api/outfitsApi/outfitsApi';
import { partnersApi } from '@/api/partnersApi/partnersApi';
import { propertyApi } from '@/api/propertyApi/propertyApi';
import { suitcasesApi } from '@/api/suitcasesApi/suitcasesApi';
import { userApi } from '@/api/userApi/userApi';
import { weatherApi } from '@/api/weatherApi/weatherApi';

export type RootState = ReturnType<typeof rootReducer>;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export const useAppDispatch: () => ReturnType<typeof useDispatch> = () =>
    useDispatch();

const rootReducer = combineReducers({
    [authApi.reducerPath]: authApi.reducer,
    [authEmailApi.reducerPath]: authEmailApi.reducer,
    [favoritesApi.reducerPath]: favoritesApi.reducer,
    [filesApi.reducerPath]: filesApi.reducer,
    [clothingApi.reducerPath]: clothingApi.reducer,
    [propertyApi.reducerPath]: propertyApi.reducer,
    [siteConfig.reducerPath]: siteConfig.reducer,
    [progressSlice.reducerPath]: progressSlice.reducer,
    [suitcasesApi.reducerPath]: suitcasesApi.reducer,
    [citiesApi.reducerPath]: citiesApi.reducer,
    [weatherApi.reducerPath]: weatherApi.reducer,
    [eventsApi.reducerPath]: eventsApi.reducer,
    [outfitsApi.reducerPath]: outfitsApi.reducer,
    [aiApi.reducerPath]: aiApi.reducer,
    [partnersApi.reducerPath]: partnersApi.reducer,
    [storiesSlice.reducerPath]: storiesSlice.reducer,
    [pauseSlice.reducerPath]: pauseSlice.reducer,
    [userApi.reducerPath]: userApi.reducer,
    [viewedStoriesSlice.reducerPath]: viewedStoriesSlice.reducer,
    [authSlice.reducerPath]: authSlice.reducer,
});

export const store: Store<RootState> = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware().concat([
            authApi.middleware,
            authEmailApi.middleware,
            userApi.middleware,
            favoritesApi.middleware,
            filesApi.middleware,
            clothingApi.middleware,
            propertyApi.middleware,
            suitcasesApi.middleware,
            citiesApi.middleware,
            weatherApi.middleware,
            eventsApi.middleware,
            outfitsApi.middleware,
            aiApi.middleware,
            partnersApi.middleware,
        ]),
});
